/**
 * Main navigation registry for the unified console. Seven destinations cover
 * both apps: assets (4 finance views), knowledge (index/favorites), inbox,
 * messages, hardware (projects/BOM/prices), health (metrics/reports) and more.
 */

import type { ReactNode } from 'react'
import { IconBell, IconChart, IconInbox, IconLibrary, IconMore, IconPulse, IconWrench } from '../components'
import type { TabId } from '../store'

export type TabDef = {
  id: TabId
  path: string
  label: string
  icon: ReactNode
}

export type KbSubnavId = 'inbox' | 'index' | 'favorites' | 'review' | 'summary' | 'search' | 'v3'

export type KbSubnavDef = {
  id: KbSubnavId
  path: string
  label: string
}

/** Secondary navigation shown under every knowledge-base page. */
export const KB_SUBNAV: KbSubnavDef[] = [
  { id: 'inbox', path: '/kb/inbox', label: '收集箱' },
  { id: 'index', path: '/kb/index', label: '索引' },
  { id: 'favorites', path: '/kb/favorites', label: '收藏' },
  { id: 'review', path: '/kb/review', label: '每日回顾' },
  { id: 'summary', path: '/kb/summary', label: '每日摘要' },
  { id: 'search', path: '/kb/search', label: '语义搜索' },
  { id: 'v3', path: '/kb/v3', label: 'v3 资源' },
]

export const TABS: TabDef[] = [
  { id: 'assets', path: '/assets/overview', label: '资产', icon: <IconPulse size={18} /> },
  { id: 'knowledge', path: '/kb/index', label: '知识', icon: <IconLibrary size={18} /> },
  { id: 'inbox', path: '/inbox', label: '收件箱', icon: <IconInbox size={18} /> },
  { id: 'messages', path: '/messages', label: '消息', icon: <IconBell size={18} /> },
  { id: 'more', path: '/more', label: '更多', icon: <IconMore size={18} /> },
  { id: 'hardware', path: '/hardware', label: '硬件', icon: <IconWrench size={18} /> },
  { id: 'health', path: '/health', label: '健康', icon: <IconChart size={18} /> },
]

const PATH_BY_TAB = new Map<TabId, string>(TABS.map((tab) => [tab.id, tab.path]))

export const DEFAULT_PATH = '/assets/overview'

export function pathForTab(id: TabId): string {
  const path = PATH_BY_TAB.get(id)
  if (path === undefined) throw new Error(`unknown tab id: ${id}`)
  return path
}

export function tabForPath(pathname: string): TabId | undefined {
  if (pathname.startsWith('/assets')) return 'assets'
  if (pathname.startsWith('/kb')) return 'knowledge'
  if (pathname.startsWith('/inbox')) return 'inbox'
  if (pathname.startsWith('/messages')) return 'messages'
  if (pathname.startsWith('/hardware')) return 'hardware'
  if (pathname.startsWith('/health')) return 'health'
  if (pathname.startsWith('/more')) return 'more'
  return undefined
}

export function isTabId(value: string): value is TabId {
  return PATH_BY_TAB.has(value as TabId)
}
